import { linearToRgb, luminance, oklabToLinearRGB, rgbToOKLab } from '@limonade/color-science';
const clamp = (v, min = 0, max = 1) => Math.max(min, Math.min(max, v));
/** Continuous tone-conditioned palette; never hard quantize pixels to swatches. */
export function createPaletteColorCorrection(source, colors, options = {}) {
    if (!source.length || colors.length < 2)
        throw new Error('Palette matching requires source samples and at least two palette colors.');
    if ([...source, ...colors].some((color) => ![color.r, color.g, color.b].every(Number.isFinite)))
        throw new Error('Palette matching requires finite RGB samples.');
    const levels = source.map((rgb) => rgbToOKLab(rgb).l).sort((a, b) => a - b);
    const targets = colors.map(rgbToOKLab).sort((a, b) => a.l - b.l);
    const anchors = targets.map((target, i) => ({
        sourceLightness: levels[Math.min(levels.length - 1, Math.floor((i + 0.5) / targets.length * levels.length))], target,
    }));
    // Flat frames have no usable quantiles. Use a smooth absolute-lightness map,
    // not coincident knots (which would create jumps in the exported LUT).
    if (anchors.at(-1).sourceLightness - anchors[0].sourceLightness < 0.02)
        anchors.forEach((anchor, i) => { anchor.sourceLightness = (i + 0.5) / anchors.length; });
    // Consolidate equal source levels into one averaged target to avoid discontinuities.
    const unique = [];
    for (let i = 0; i < anchors.length;) {
        let j = i + 1;
        while (j < anchors.length && anchors[j].sourceLightness - anchors[i].sourceLightness < 1e-5)
            j++;
        const group = anchors.slice(i, j), n = group.length;
        unique.push({ sourceLightness: anchors[i].sourceLightness,
            target: { l: group.reduce((s, a) => s + a.target.l, 0) / n,
                a: group.reduce((s, a) => s + a.target.a, 0) / n, b: group.reduce((s, a) => s + a.target.b, 0) / n } });
        i = j;
    }
    return { schema: 'limonade-pce-palette-correction/v1', anchors: unique,
        lightnessMode: options.matchLightness ? 'palette-distribution' : 'preserve-source', monochrome: Boolean(options.monochrome) };
}
function targetAt(correction, lightness) {
    const anchors = correction.anchors;
    if (!anchors.length)
        throw new Error('Palette correction has no anchors.');
    if (lightness <= anchors[0].sourceLightness)
        return anchors[0].target;
    for (let i = 1; i < anchors.length; i++) {
        const high = anchors[i], low = anchors[i - 1];
        if (lightness > high.sourceLightness)
            continue;
        const t = clamp((lightness - low.sourceLightness) / Math.max(1e-6, high.sourceLightness - low.sourceLightness));
        return { l: low.target.l + (high.target.l - low.target.l) * t,
            a: low.target.a + (high.target.a - low.target.a) * t, b: low.target.b + (high.target.b - low.target.b) * t };
    }
    return anchors.at(-1).target;
}
function gamutMapped(color) {
    // Leave 8-bit highlight headroom when introducing chroma. Mapping right onto
    // a channel's 1.0 boundary can create new clipped patches in the real CUBE
    // even though perceived lightness is preserved. True near-white stays white.
    const ceiling = Math.max(0.98, clamp(color.l) ** 3);
    const inside = (rgb) => Math.min(rgb.r, rgb.g, rgb.b) >= -1e-7 && Math.max(rgb.r, rgb.g, rgb.b) <= ceiling + 1e-7;
    let linear = oklabToLinearRGB(color);
    if (!inside(linear)) {
        // Hue-preserving chroma compression at fixed perceived lightness. A bounded
        // search only during sampled analysis/LUT baking, not during host rendering.
        let low = 0, high = 1;
        for (let i = 0; i < 12; i++) {
            const scale = (low + high) / 2;
            const rgb = oklabToLinearRGB({ l: color.l, a: color.a * scale, b: color.b * scale });
            if (inside(rgb))
                low = scale;
            else
                high = scale;
        }
        linear = oklabToLinearRGB({ l: color.l, a: color.a * low, b: color.b * low });
    }
    return { r: linearToRgb(clamp(linear.r)), g: linearToRgb(clamp(linear.g)), b: linearToRgb(clamp(linear.b)) };
}
export function createPaletteColorTransform(correction) {
    if (correction.schema !== 'limonade-pce-palette-correction/v1' || !correction.anchors.length || correction.anchors.length > 256 ||
        !['preserve-source', 'palette-distribution'].includes(correction.lightnessMode) || typeof correction.monochrome !== 'boolean' ||
        correction.anchors.some((a, i) => ![a.sourceLightness, a.target.l, a.target.a, a.target.b].every(Number.isFinite) ||
            a.sourceLightness < 0 || a.sourceLightness > 1 || a.target.l < 0 || a.target.l > 1 ||
            (i > 0 && a.sourceLightness <= correction.anchors[i - 1].sourceLightness)))
        throw new Error('Invalid palette correction: expected bounded, finite, ordered OKLab anchors.');
    if (correction.monochrome)
        return (rgb) => {
            // Exact achromatic output, preserving linear-light Y. No saturation floor,
            // hue layer, skin protection or audit fallback can reintroduce color.
            const gray = linearToRgb(luminance(rgb));
            return { r: gray, g: gray, b: gray };
        };
    return (rgb) => {
        const original = rgbToOKLab(rgb), target = targetAt(correction, original.l);
        let l = clamp(original.l);
        if (correction.lightnessMode === 'palette-distribution') {
            // Monotone map plus a small original-detail component. Smooth endpoint
            // extensions preserve absolute black/white, not flat swatch plateaus.
            const first = correction.anchors[0], last = correction.anchors.at(-1);
            const mapped = l < first.sourceLightness ? target.l * l / Math.max(1e-6, first.sourceLightness)
                : l > last.sourceLightness ? last.target.l + (1 - last.target.l) * (l - last.sourceLightness) / Math.max(1e-6, 1 - last.sourceLightness) : target.l;
            l = clamp(l * 0.15 + mapped * 0.85);
        }
        return gamutMapped({ l, a: target.a, b: target.b });
    };
}
/** Actual-output color adherence, not photographic/semantic similarity.
 * Original tone quantiles are reused by the audit for every candidate.
 */
export function measurePaletteMatch(samples, correction) {
    if (!samples.length)
        return { scoreKind: 'palette-adherence', colorAdherenceScore: 0, meanChromaDistance: 0, sampleCount: 0 };
    const transform = createPaletteColorTransform(correction);
    const colorTarget = correction.lightnessMode === 'palette-distribution' ? {
        ...correction, lightnessMode: 'preserve-source',
        anchors: correction.anchors.map((anchor) => ({ ...anchor,
            sourceLightness: 0.15 * anchor.sourceLightness + 0.85 * anchor.target.l })),
    } : correction;
    const targetTransform = correction.monochrome ? transform : createPaletteColorTransform(colorTarget);
    let total = 0;
    for (const rgb of samples) {
        const actual = rgbToOKLab(rgb), desired = rgbToOKLab(targetTransform(rgb));
        total += Math.hypot(actual.a - desired.a, actual.b - desired.b);
    }
    const meanChromaDistance = total / samples.length;
    return { scoreKind: 'palette-adherence', colorAdherenceScore: Math.exp(-meanChromaDistance * 20), meanChromaDistance, sampleCount: samples.length };
}
//# sourceMappingURL=PaletteColorMatcher.js.map